/**
 * Paginación de las tablas del **portal del cliente**.
 *
 * Portado de `edsuite-web/src/hooks/use-paginacion.ts`: la API del portal sigue
 * devolviendo el listado completo (lo necesitan los totales y el CSV), así que
 * el corte de páginas se hace **en el navegador**.
 *
 * Detalles:
 * - Los totales y contadores se calculan sobre el listado completo; el hook sólo
 *   decide qué filas se ven.
 * - La página se recorta al rango válido: si al buscar quedan menos páginas se
 *   muestra la última en lugar de una tabla vacía.
 * - `clave` reinicia a la primera página cuando cambia (búsqueda, filtros o
 *   pestaña rápida). Debe ser un valor **estable** (texto/número), no un objeto
 *   nuevo en cada render.
 * - El tamaño de página se recuerda en `localStorage` con su propia clave, para
 *   no mezclarse con el del panel de operadores.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';

/** Tamaños ofrecidos en el selector (`0` = todas las filas). */
export const TAMANOS_PAGINA = [10, 25, 50, 100, 0] as const;

const CLAVE_TAMANO = 'edsuite-portal:filas-por-pagina';

/** Filas por página por defecto (el usuario puede cambiarlo y se recuerda). */
const TAMANO_POR_DEFECTO = 10;

/** Tamaño guardado, si es uno de los válidos. */
function tamanoGuardado(): number {
  if (typeof window === 'undefined') return TAMANO_POR_DEFECTO;

  const crudo = window.localStorage.getItem(CLAVE_TAMANO);

  // Ojo: `Number(null)` es 0, que es el valor de «Todas»; hay que mirar el texto.
  if (crudo === null) return TAMANO_POR_DEFECTO;

  const guardado = Number(crudo);

  return (TAMANOS_PAGINA as readonly number[]).includes(guardado) ? guardado : TAMANO_POR_DEFECTO;
}

export interface OpcionesPaginacion {
  /** Tamaño inicial (manda sobre el preferido guardado). */
  porPagina?: number;
  /** Valor que reinicia la paginación cuando cambia (búsqueda, filtros…). */
  clave?: unknown;
}

export interface Paginacion<T> {
  /** Las filas de la página actual. */
  visibles: T[];
  /** Todas las filas recibidas. */
  total: number;
  /** Página actual (1..paginas). */
  pagina: number;
  paginas: number;
  /** Filas por página (`0` = todas). */
  tamano: number;
  /** Número de la primera fila visible (1..total) y de la última. */
  desde: number;
  hasta: number;
  /** Hay más filas de las que caben en una página. */
  hayPaginas: boolean;
  irA: (pagina: number) => void;
  cambiarTamano: (tamano: number) => void;
}

/** Reparte un listado en páginas y devuelve la página actual. */
export function usePaginacion<T>(filas: T[], opciones: OpcionesPaginacion = {}): Paginacion<T> {
  const { porPagina, clave } = opciones;

  const [tamano, setTamano] = useState(() => porPagina ?? tamanoGuardado());
  const [pagina, setPagina] = useState(1);

  const total = filas.length;
  const efectivo = tamano === 0 ? Math.max(total, 1) : tamano;
  const paginas = Math.max(1, Math.ceil(total / efectivo));
  const actual = Math.min(Math.max(pagina, 1), paginas);

  // Búsqueda o filtros nuevos: se vuelve a la primera página.
  useEffect(() => {
    setPagina(1);
  }, [clave]);

  // Si el listado se acorta (o crece), la página guardada puede quedar fuera de rango.
  useEffect(() => {
    if (pagina !== actual) setPagina(actual);
  }, [pagina, actual]);

  const desde = (actual - 1) * efectivo;
  const visibles = useMemo(() => filas.slice(desde, desde + efectivo), [filas, desde, efectivo]);

  const cambiarTamano = useCallback((nuevo: number) => {
    setTamano(nuevo);
    setPagina(1);
    recordarTamano(nuevo);
  }, []);

  return {
    visibles,
    total,
    pagina: actual,
    paginas,
    tamano,
    desde: total === 0 ? 0 : desde + 1,
    hasta: desde + visibles.length,
    hayPaginas: tamano !== 0 && total > tamano,
    irA: setPagina,
    cambiarTamano,
  };
}

/** Recuerda el tamaño de página elegido para las demás pantallas del portal. */
export function recordarTamano(tamano: number): void {
  if (typeof window === 'undefined') return;

  window.localStorage.setItem(CLAVE_TAMANO, String(tamano));
}
